import { useState } from "react";

import { errorMessage, useShortenWorkspace } from "../../api/queries";
import type { Workspace } from "../../api/types";
import { formatRemaining, remainingUntil } from "../../lib/format";
import { Dialog } from "../ui/Dialog";

/** How much longer you might need it, offered as pills. */
const PRESETS: readonly { minutes: number; pill: string; spoken: string }[] = [
  { minutes: 30, pill: "30 min", spoken: "30 minutes" },
  { minutes: 60, pill: "1 h", spoken: "1 hour" },
  { minutes: 120, pill: "2 h", spoken: "2 hours" },
  { minutes: 240, pill: "4 h", spoken: "4 hours" },
];

const MINUTE_MS = 60_000;

interface ShortenDialogProps {
  workspace: Workspace;
  onClose: () => void;
}

/**
 * Hand the GPUs back early, with everything still running.
 *
 * The counterpart of "Keep it longer", and the reason it exists: until now the
 * only way to release GPUs before the deadline was to stop the workspace,
 * which kills whatever is running in it. Here the workspace carries on — same
 * shell, same processes — and simply ends sooner.
 *
 * The copy makes that easy and never makes it a duty. Nobody is metered, and a
 * researcher who is not sure should leave the deadline alone.
 *
 * Args:
 *     workspace: The workspace whose deadline moves in.
 *     onClose: Called when the dialog is dismissed.
 *
 * Returns:
 *     The dialog.
 */
export function ShortenDialog({
  workspace,
  onClose,
}: ShortenDialogProps): JSX.Element {
  const shorten = useShortenWorkspace(workspace.id);
  const remaining = remainingUntil(workspace.expires_at) ?? 0;

  // Only offer what actually gives time back. Asking for longer than is left
  // is an extension, and the server says so rather than doing it quietly.
  const choices = PRESETS.filter(
    (preset) => preset.minutes * MINUTE_MS < remaining,
  );
  const [minutes, setMinutes] = useState<number>(
    choices[choices.length - 1]?.minutes ?? 0,
  );
  const chosen = choices.find((preset) => preset.minutes === minutes);

  // The final countdown is however long this deployment warns for, read from
  // the workspace itself rather than assumed: the gap between its deadline and
  // its warning. Landing inside it is allowed and correct — it just must not
  // arrive as a surprise.
  const warningWindow =
    workspace.expires_at && workspace.warning_at
      ? Date.parse(workspace.expires_at) - Date.parse(workspace.warning_at)
      : 0;
  const entersCountdown =
    chosen !== undefined && chosen.minutes * MINUTE_MS <= warningWindow;

  return (
    <Dialog
      title="Give the GPUs back earlier"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          {chosen ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={shorten.isPending}
              onClick={() =>
                shorten.mutate({ minutes }, { onSuccess: () => onClose() })
              }
            >
              {shorten.isPending
                ? "Setting the new time…"
                : `End it in ${chosen.spoken}`}
            </button>
          ) : null}
        </>
      }
    >
      {choices.length === 0 ? (
        <p className="dialog__text">
          This workspace already ends in {formatRemaining(workspace.expires_at)}
          , so there is nothing left to hand back. Stop it if you are done —
          your home folder is kept either way.
        </p>
      ) : (
        <>
          <p className="dialog__text">
            It runs for another {formatRemaining(workspace.expires_at)}. If you
            know you will be done sooner, set a nearer end: nothing you are
            running is interrupted, your session stays exactly as it is, and
            your {workspace.gpus === 1 ? "GPU goes" : "GPUs go"} back to the
            pool the moment it ends — for whoever is waiting.
          </p>
          <fieldset className="choice-row">
            <legend className="sr-only">How much longer do you need it</legend>
            {choices.map((preset) => (
              <label
                key={preset.minutes}
                className="choice"
                data-selected={minutes === preset.minutes}
              >
                <input
                  type="radio"
                  name="shorten-minutes"
                  className="sr-only"
                  value={preset.minutes}
                  checked={minutes === preset.minutes}
                  aria-label={preset.spoken}
                  onChange={() => setMinutes(preset.minutes)}
                />
                {preset.pill}
              </label>
            ))}
          </fieldset>
          {entersCountdown ? (
            <p className="dialog__warning" role="note">
              That is inside the final countdown, so the workspace will show as
              ending soon straight away. Nothing stops before the time you
              picked, and keeping it longer puts it back if you change your
              mind.
            </p>
          ) : null}
          {workspace.idle_since ? (
            <p className="dialog__warning" role="note">
              The idle countdown already running keeps running — this says you
              are nearly done, not that you still need the GPUs. If they stay
              unused it can end the workspace before the time you pick here.
            </p>
          ) : null}
          <p className="dialog__text">
            Entirely up to you. There is no allowance and nothing is counted;
            if you are not sure, leave the deadline where it is and give the
            time back later.
          </p>
        </>
      )}
      {shorten.isError ? (
        <p className="dialog__error" role="alert">
          {errorMessage(shorten.error)}
        </p>
      ) : null}
    </Dialog>
  );
}
